import { useState, useEffect, useCallback, Fragment } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AppNav } from "@/components/AppNav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { ClipboardCheck, Loader2, Plus, Check, ExternalLink, Search, FileSpreadsheet } from "lucide-react";
import { exportChecklistCsv } from "@/lib/teamPerfExport";

const isUrl = (v) => /^https?:\/\//i.test(v || "");

function AddPropertyDialog({ open, onOpenChange, onCreated }) {
  const [name, setName] = useState("");
  const [location, setLocation] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/properties", { name, location });
      toast.success(`Property "${name}" ditambahkan`);
      setName(""); setLocation("");
      onOpenChange(false);
      onCreated();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Add property</DialogTitle>
          <DialogDescription>Property baru otomatis muncul di checklist, Registry, dan modul lain.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="space-y-1.5">
            <Label>Property name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} required placeholder="Villa ..." data-testid="checklist-new-property-name" />
          </div>
          <div className="space-y-1.5">
            <Label>Location (optional)</Label>
            <Input value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Sanur, Bali" data-testid="checklist-new-property-location" />
          </div>
          <Button type="submit" className="w-full" disabled={saving} data-testid="checklist-new-property-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add property"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function CellDialog({ edit, onClose, onSaved }) {
  const [value, setValue] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { setValue(edit?.value || ""); }, [edit]);
  if (!edit) return null;
  const save = async (v) => {
    setSaving(true);
    try {
      await api.put(`/channel-checklist/${edit.propertyId}`, { key: edit.key, value: v });
      toast.success(v ? "Tersimpan" : "Dikosongkan");
      onSaved();
      onClose();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{edit.label} · {edit.propertyName}</DialogTitle>
          <DialogDescription>{edit.key === "remark" ? "Catatan untuk property ini." : "Isi link atau ID listing. Kosongkan untuk menandai belum ada."}</DialogDescription>
        </DialogHeader>
        <Input value={value} onChange={(e) => setValue(e.target.value)} placeholder={edit.key === "remark" ? "Catatan..." : "https://... atau ID"} autoFocus
          onKeyDown={(e) => e.key === "Enter" && save(value)} data-testid="checklist-cell-input" />
        {isUrl(value) && (
          <a href={value} target="_blank" rel="noreferrer" className="text-xs text-[#D96635] inline-flex items-center gap-1 hover:underline" data-testid="checklist-cell-open-link">
            <ExternalLink className="h-3 w-3" /> Buka link
          </a>
        )}
        <div className="flex gap-2">
          {edit.value && (
            <Button variant="outline" className="flex-1" disabled={saving} onClick={() => save("")} data-testid="checklist-cell-clear">Kosongkan</Button>
          )}
          <Button className="flex-1" disabled={saving} onClick={() => save(value)} data-testid="checklist-cell-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Simpan"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function ChannelChecklistPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const dark = isAdmin;
  const [data, setData] = useState({ groups: [], rows: [] });
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [q, setQ] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/channel-checklist");
      setData(data);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to load checklist"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const columns = data.groups.flatMap((g) => g.columns);
  const rows = data.rows.filter((r) => r.property_name.toLowerCase().includes(q.toLowerCase()));
  const sub = dark ? "text-slate-400" : "text-slate-500";
  const border = dark ? "border-slate-800" : "border-slate-200";
  const headCls = `border ${border} px-2 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-center ${dark ? "bg-[#2A3A22] text-[#C9D9A8]" : "bg-[#E6EFD6] text-[#3D5229]"}`;

  const openCell = (r, key, label) => setEdit({ propertyId: r.property_id, propertyName: r.property_name, key, label, value: r.cells[key] || "" });

  return (
    <div className={`min-h-screen ${dark ? "bg-[#1F1B16] dark-scroll text-slate-100" : "bg-[#FAF7F2]"}`}>
      <AppNav />
      <main className="max-w-[1600px] mx-auto px-4 sm:px-6 py-8" data-testid="channel-checklist-page">
        <div className="mb-6 flex items-end justify-between gap-4 flex-wrap">
          <div>
            <h1 className={`font-display text-2xl font-bold flex items-center gap-2 ${dark ? "text-white" : "text-slate-900"}`}>
              <ClipboardCheck className="h-6 w-6 text-[#D96635]" /> Channel Check-list
            </h1>
            <p className={`text-sm ${sub}`}>Klik sel untuk isi link / ID. Property baru (termasuk dari kontrak partner yang ditandatangani) otomatis muncul di sini.</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" disabled={loading || !data.rows.length}
              onClick={() => exportChecklistCsv(data.groups, data.rows)}
              className={dark ? "bg-slate-800 border-slate-700 text-slate-200 hover:bg-slate-700" : ""}
              data-testid="checklist-export-csv">
              <FileSpreadsheet className="h-4 w-4" /> CSV
            </Button>
            <div className="relative">
              <Search className={`h-4 w-4 absolute left-2.5 top-2.5 ${sub}`} />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari property" className={`pl-8 w-52 ${dark ? "bg-slate-900 border-slate-700 text-slate-100" : ""}`} data-testid="checklist-search" />
            </div>
            {isAdmin && (
              <Button onClick={() => setAddOpen(true)} className="bg-[#D96635] hover:bg-[#C2572B] text-white" data-testid="checklist-add-property-btn">
                <Plus className="h-4 w-4" /> Add property
              </Button>
            )}
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[#D96635]" /></div>
        ) : (
          <div className={`rounded-xl border ${border} overflow-auto max-h-[75vh]`}>
            <table className="text-sm border-collapse min-w-full" data-testid="checklist-table">
              <thead className="sticky top-0 z-20">
                <tr>
                  <th rowSpan={3} className={`${headCls} sticky left-0 z-30 min-w-[220px] text-left`}>Property name</th>
                  <th colSpan={columns.length} className={headCls}>Check - list</th>
                  <th rowSpan={3} className={`${headCls} min-w-[180px]`}>Remark</th>
                </tr>
                <tr>
                  {data.groups.map((g) => <th key={g.id} colSpan={g.columns.length} className={headCls}>{g.label}</th>)}
                </tr>
                <tr>
                  {columns.map((c) => <th key={c.key} className={`${headCls} min-w-[84px]`}>{c.label}</th>)}
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr><td colSpan={columns.length + 2} className={`text-center py-10 ${sub}`} data-testid="checklist-empty">Belum ada property.</td></tr>
                )}
                {rows.map((r) => {
                  const filled = columns.filter((c) => r.cells[c.key]).length;
                  return (
                    <tr key={r.property_id} className={dark ? "hover:bg-slate-800/30" : "hover:bg-white"} data-testid={`checklist-row-${r.property_id}`}>
                      <td className={`border ${border} px-3 py-2 sticky left-0 z-10 whitespace-nowrap ${dark ? "bg-[#1F1B16] text-slate-100" : "bg-[#FAF7F2] text-slate-900"}`}>
                        <div className="font-medium">{r.property_name}</div>
                        <div className={`text-[10px] font-mono ${sub}`}>{filled}/{columns.length} terisi</div>
                      </td>
                      {columns.map((c) => {
                        const v = r.cells[c.key];
                        return (
                          <td key={c.key} className={`border ${border} p-0 text-center`}>
                            <button onClick={() => openCell(r, c.key, c.label)} title={v || "Belum diisi"}
                              className={`w-full h-10 flex items-center justify-center transition-colors ${v ? (dark ? "bg-emerald-500/10 hover:bg-emerald-500/20" : "bg-emerald-50 hover:bg-emerald-100") : (dark ? "hover:bg-slate-800" : "hover:bg-slate-100")}`}
                              data-testid={`checklist-cell-${r.property_id}-${c.key}`}>
                              {v ? <Check className="h-4 w-4 text-emerald-500" /> : <span className={`text-xs ${dark ? "text-slate-700" : "text-slate-300"}`}>+</span>}
                            </button>
                          </td>
                        );
                      })}
                      <td className={`border ${border} p-0`}>
                        <button onClick={() => openCell(r, "remark", "Remark")}
                          className={`w-full min-h-10 px-2 py-2 text-left text-xs ${dark ? "text-slate-300 hover:bg-slate-800" : "text-slate-600 hover:bg-slate-100"}`}
                          data-testid={`checklist-remark-${r.property_id}`}>
                          {r.cells.remark || <span className={dark ? "text-slate-700" : "text-slate-300"}>+</span>}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </main>
      <CellDialog edit={edit} onClose={() => setEdit(null)} onSaved={load} />
      {isAdmin && <AddPropertyDialog open={addOpen} onOpenChange={setAddOpen} onCreated={load} />}
    </div>
  );
}
